import React, {
  FunctionComponent, useState, useCallback, useMemo,
} from 'react';
import { useRouter } from 'next/router';
import LeadForm from '../../components/lead-form/LeadForm';
import { saveConsultancy } from '../../../services/consultancy/consultancy';
import withNextConfig from '../../hocs/withNextConfig/withNextConfig';
import { GoogleMapsContext } from '../../contexts/google-map-context/GoogleMapsContext';
import useAutoCompleteSessionToken from '../../hooks/useAutoCompleteSessionToken/useAutoCompleteSessionToken';
import { Consultancy, ConsultancyType } from '../../../services/dtos/consultancy';
import getTrafficSource from '../../utils/traffic-source/getTrafficSource';

export interface CreateConsultancyProps {
  consultancyType: ConsultancyType,
  config: { MAPS: { apiKey: string } },
  dataTracking?: string,
  onSuccess?: (lead: Consultancy) => void,
}

const SAVE_CONSULTANCY_ERROR_MESSAGE = 'Errore durante il salvataggio, riprova o contattaci!';

export const CreateConsultancy: FunctionComponent<CreateConsultancyProps> = ({
  consultancyType,
  config,
  dataTracking,
  onSuccess,
}) => {
  const { apiKey } = config.MAPS;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();
  const { data: sessionToken } = useAutoCompleteSessionToken(apiKey);
  const { query } = useRouter();

  const googleMapsContext = useMemo(() => ({
    apiKey,
    sessionToken,
  }), [apiKey, sessionToken]);

  const saveValues = useCallback(async (values) => {
    setLoading(true);

    try {
      const consultancy = await saveConsultancy({
        consultancyType,
        name: values.name,
        email: values.email,
        phoneNumber: values.phoneNumber,
        address: values.address,
        consent: values.consent,
        source: getTrafficSource(query),
      });

      if (onSuccess) {
        onSuccess(consultancy);
      }
    } catch (err) {
      setError(err);
    }

    setLoading(false);
  }, [consultancyType, query, onSuccess]);

  return (
    <GoogleMapsContext.Provider
      value={googleMapsContext}
    >
      <LeadForm
        loading={loading}
        dataTracking={dataTracking}
        onSubmit={saveValues}
        errorMessage={error && SAVE_CONSULTANCY_ERROR_MESSAGE}
      />
    </GoogleMapsContext.Provider>
  );
};

export default withNextConfig(CreateConsultancy);
